import React, { Fragment } from 'react';

import { InvestorFirmOption } from '../../common/components/form/investor-firm-search/InvestorFirmSearch';
import { isValidCmgEntityKey } from '../../common/util/isValidCmgEntityKey';
import InvestorSearch from './components/investor-search/InvestorSearch';
import { useCmgEntityKeyContext } from './contexts/CmgEntityKeyContext';
import { useFirmKeyOrIdContext } from './contexts/FirmKeyOrIdContext';
import { SupersededOfferingProvider } from './contexts/SupersededOfferingContext';
import { useGetOfferingStatus } from './hooks/useGetOfferingStatus';
import { getIndication, useIndicationEntityNameQuery } from './hooks/useIndicationEntityNameQuery';
import { useInvestorFirmSearch } from './hooks/useInvestorFirmSearch';
import {
  useOfferingSidePanelMatch,
  useOfferingSidePanelRouteMatch,
} from './hooks/useOfferingSidePanelMatch';
import offeringSidePanelBuySideRouteFactory from './offeringSidePanelBuySideRouteFactory';
import OfferingSidePanelBuySideRoutes from './OfferingSidePanelBuySideRoutes';
import offeringSidePanelSellSideRouteFactory from './offeringSidePanelSellSideRouteFactory';
import OfferingSidePanelSellSideRoutes from './OfferingSidePanelSellSideRoutes';
import {
  OrderBookPersonaType,
  useIdentifyOrderBookPersona,
} from './order-book/hooks/useIdentifyOrderBookPersona';

const getInvestorFirmCmgEntityKey = (
  investorFirm?: { cmgEntityKey?: string | null; linkedCmgEntityKey?: string | null } | null
): string | undefined => {
  if (!investorFirm) {
    return undefined;
  }
  return investorFirm.cmgEntityKey ?? investorFirm.linkedCmgEntityKey ?? undefined;
};

/**
 * OfferingSidePanel displays offering data in a side panel whenever
 * the current route matches the offering side panel route
 */
const OfferingSidePanel: React.FC = () => {
  const sidePanelMatch = useOfferingSidePanelMatch();

  const offeringId = sidePanelMatch?.offeringId;
  const { type, isSellSide } = useIdentifyOrderBookPersona({
    offeringId,
  });
  const isCoveredAccount = type === OrderBookPersonaType.SALES_AND_TRADING;

  const supersededData = useGetOfferingStatus({ offeringId });

  const { isFiledOffering } = supersededData;

  const offeringRoutePath = useOfferingSidePanelRouteMatch(
    offeringSidePanelSellSideRouteFactory.offeringDetails.routePath
  );
  const notesRoutePath = useOfferingSidePanelRouteMatch(
    offeringSidePanelSellSideRouteFactory.orderBookNotes.routePath
  );

  const matchesSummary = useOfferingSidePanelRouteMatch(
    offeringSidePanelBuySideRouteFactory.offeringSummary.routePath
  );

  const matchesSellSideCommunicatedDemand = useOfferingSidePanelRouteMatch(
    offeringSidePanelSellSideRouteFactory.orderBookIndicationActivity.routePath
  );
  const matchesBuySideCommunicatedDemand = useOfferingSidePanelRouteMatch(
    offeringSidePanelBuySideRouteFactory.orderBookIndicationActivity.routePath
  );
  const sidePanelCommunicatedDemandMatch =
    matchesSellSideCommunicatedDemand || matchesBuySideCommunicatedDemand;

  const filedOffering = isFiledOffering && sidePanelCommunicatedDemandMatch;

  const shouldDisplayInvestorSearch =
    !(offeringRoutePath || notesRoutePath) && !filedOffering && !matchesSummary;

  const { cmgEntityKey: cmgEntityKeyFromUrlQueryParam, updateCmgEntityKey } =
    useCmgEntityKeyContext();

  // We need to pass firmKey/Id information to Create Indication form
  const { setFirmKeyOrId, firmKeyOrId, firmKey, setFirmKey } = useFirmKeyOrIdContext();

  const { data: entityInfo } = useIndicationEntityNameQuery({
    variables: {
      offeringId: offeringId!,
      cmgEntityKey: cmgEntityKeyFromUrlQueryParam,
    },
    skip: !offeringId || !isValidCmgEntityKey(cmgEntityKeyFromUrlQueryParam),
    isCoveredAccount,
  });

  // if there is existing indication, use bankInvestorKey from the returned indication
  const indicationInfo = getIndication(entityInfo);
  const bankInvestorKey = indicationInfo
    ? indicationInfo.investorInformation.bankInvestorKey
    : firmKey;

  const { data: investorFirm } = useInvestorFirmSearch(
    bankInvestorKey
      ? { firmKeyOrId: bankInvestorKey, cmgEntityKey: cmgEntityKeyFromUrlQueryParam }
      : { cmgEntityKey: cmgEntityKeyFromUrlQueryParam },
    isCoveredAccount
  );

  React.useEffect(() => {
    if (!firmKeyOrId) {
      setFirmKeyOrId(investorFirm?.firmKey ?? investorFirm?.id ?? undefined);
    }
    if (!firmKey) {
      setFirmKey(investorFirm?.firmKey ?? undefined);
    }
  }, [investorFirm, setFirmKeyOrId, firmKeyOrId, firmKey, setFirmKey]);

  const displayInvestorSearch = isSellSide && shouldDisplayInvestorSearch;

  const getInvestorSearchValue = React.useMemo(() => {
    return (investorFirm || indicationInfo) && cmgEntityKeyFromUrlQueryParam
      ? {
          label:
            indicationInfo?.investorInformation?.bankInvestorName! ??
            indicationInfo?.investorInformation?.cmgEntityName! ??
            investorFirm?.name!,
          value:
            indicationInfo?.investorInformation?.bankInvestorName! ??
            indicationInfo?.investorInformation?.bankInvestorKey! ??
            getInvestorFirmCmgEntityKey(investorFirm),
        }
      : null;
  }, [cmgEntityKeyFromUrlQueryParam, indicationInfo, investorFirm]);

  return (
    <div data-testid="offering-side-panel">
      <SupersededOfferingProvider data={supersededData}>
        <Fragment>
          {/* We display the investor search on all routes except for offering details */}
          {displayInvestorSearch && (
            <InvestorSearch
              value={getInvestorSearchValue}
              onChange={(investor: InvestorFirmOption | null) => {
                updateCmgEntityKey(investor?.value || undefined);
                setFirmKeyOrId(investor?.firmKey || investor?.id || undefined);
                setFirmKey(investor?.firmKey || undefined);
              }}
              isCoveredAccount={isCoveredAccount}
            />
          )}
          <div>
            {isSellSide ? (
              <OfferingSidePanelSellSideRoutes offeringId={offeringId} />
            ) : (
              <OfferingSidePanelBuySideRoutes />
            )}
          </div>
        </Fragment>
      </SupersededOfferingProvider>
    </div>
  );
};

export default OfferingSidePanel;
